/**
 * ============================================================
 * IZAPA — Gift with Purchase (GWP)
 * ============================================================
 * JS puro, sin dependencias. Usa la Cart AJAX API de Shopify
 * (/cart.js, /cart/add.js, /cart/change.js) para:
 *
 *   1. Mostrar un selector de regalo cuando se cumple CUALQUIERA de
 *      estas dos condiciones (sin contar el regalo mismo):
 *        - el carrito tiene 2 o más productos (data-min-qty), o
 *        - el subtotal alcanza el umbral definido (data-threshold),
 *      ambos configurables en el snippet gift-with-purchase.liquid.
 *   2. Mostrar una VENTANA EMERGENTE (modal) automáticamente en el
 *      momento exacto en que el carrito cruza ese umbral, para que
 *      sea imposible no notarlo.
 *   3. Si la persona cierra la ventana sin elegir, dejar visible el
 *      selector normal dentro del carrito (drawer y/o página de
 *      carrito) como recordatorio — un poco más grande que antes.
 *   4. Dejar que la persona elija UNA de las dos lociones de 30 ml
 *      como regalo.
 *   5. Quitar el regalo automáticamente si el subtotal cae por
 *      debajo del umbral (por ejemplo, al eliminar un producto).
 *   6. Garantizar máximo 1 regalo por pedido — si se elige otro
 *      aroma, se reemplaza en vez de acumularse.
 *
 * Este archivo es intencionalmente agnóstico al tema: toda su
 * configuración (umbral, Variant IDs, secciones a refrescar) se
 * lee de los atributos data-* del contenedor #gwp-widget, que
 * renderiza snippets/gift-with-purchase.liquid. Puede haber más de
 * una instancia de #gwp-widget en la página (una en el cart drawer,
 * otra en la página /cart) — este script mantiene sincronizadas a
 * todas.
 * ============================================================
 */
(function () {
  'use strict';

  const WIDGET_SELECTOR = '#gwp-widget';
  const PROGRESS_SELECTOR = '#gwp-progress';
  const GIFT_PROPERTY_KEY = '_gift'; // El prefijo "_" oculta la propiedad
  // en el carrito/checkout visibles al cliente, pero sigue siendo visible
  // para el staff en el detalle del pedido en el Admin.
  const MODAL_FLAG_KEY = 'gwp_needs_gift'; // sessionStorage: evita que la
  // ventana emergente se repita en cada sincronización mientras el
  // carrito siga calificando sin regalo elegido — solo vuelve a salir
  // cuando el carrito "cruza" el umbral de nuevo (por ejemplo, bajó y
  // volvió a subir, o se quitó el regalo elegido).

  let widgets = [];
  let progressEls = [];
  let modalOverlay = null;
  let isSyncing = false;
  let pendingResync = false;

  function init() {
    widgets = Array.from(document.querySelectorAll(WIDGET_SELECTOR));
    if (!widgets.length) return; // El snippet no está presente en esta página
    progressEls = Array.from(document.querySelectorAll(PROGRESS_SELECTOR));

    bindPickerEvents();
    buildModal();
    patchFetchForCartSync();
    bindKnownThemeEvents();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', syncFromCart);
    } else {
      syncFromCart();
    }
    window.addEventListener('pageshow', syncFromCart); // back/forward cache
  }

  function config() {
    const widget = widgets[0];
    return {
      thresholdCents: parseInt(widget.dataset.threshold, 10) || 0,
      minQty: parseInt(widget.dataset.minQty, 10) || 0,
      variantA: widget.dataset.giftVariantA,
      variantB: widget.dataset.giftVariantB,
      refreshSections: (widget.dataset.refreshSections || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    };
  }

  /* ------------------------------------------------------------ *
   * 1) Mantenerse sincronizado con el carrito, lo cambie quien lo
   *    cambie (nuestro widget o los propios controles del tema).
   * ------------------------------------------------------------ */

  // Cualquier mutación AJAX del carrito en la página — la nuestra o la
  // de los steppers de cantidad / botones de eliminar del propio tema —
  // pasa por window.fetch hacia un endpoint /cart/*.js. Interceptar
  // fetch (en vez de adivinar los eventos personalizados del tema) hace
  // que esto siga funcionando sin importar cómo esté armado el resto
  // del tema.
  function patchFetchForCartSync() {
    const originalFetch = window.fetch;
    window.fetch = function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      const isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?/.test(url);
      const result = originalFetch.apply(this, args);
      if (isCartMutation) {
        result
          .then(() => {
            // Pequeño delay para dejar que Shopify persista el cambio.
            setTimeout(syncFromCart, 50);
          })
          .catch(() => {});
      }
      return result;
    };
  }

  // Además, si tu tema ya dispara alguno de estos eventos personalizados
  // al actualizar el carrito, los escuchamos también (no hace daño tenerlo
  // duplicado con el patch de fetch de arriba).
  function bindKnownThemeEvents() {
    ['cart:updated', 'cart:change', 'theme:cart:change'].forEach((eventName) => {
      document.addEventListener(eventName, syncFromCart);
    });
  }

  async function syncFromCart() {
    try {
      const cart = await fetchCart();
      await applyRules(cart);
    } catch (err) {
      console.error('[GWP] No se pudo sincronizar el carrito:', err);
    }
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  function findGiftLine(cart) {
    return cart.items.find(
      (item) => item.properties && item.properties[GIFT_PROPERTY_KEY] === 'true'
    );
  }

  function nonGiftSubtotalCents(cart) {
    return cart.items.reduce((sum, item) => {
      const isGift = item.properties && item.properties[GIFT_PROPERTY_KEY] === 'true';
      return isGift ? sum : sum + item.line_price;
    }, 0);
  }

  // Suma de unidades en el carrito sin contar la línea de regalo — usada
  // para la regla de "2 o más productos", sea cual sea su precio.
  function nonGiftQuantity(cart) {
    return cart.items.reduce((sum, item) => {
      const isGift = item.properties && item.properties[GIFT_PROPERTY_KEY] === 'true';
      return isGift ? sum : sum + item.quantity;
    }, 0);
  }

  function cartQualifies(cart) {
    const { thresholdCents, minQty } = config();
    const subtotal = nonGiftSubtotalCents(cart);
    const qty = nonGiftQuantity(cart);
    return subtotal >= thresholdCents || (minQty > 0 && qty >= minQty);
  }

  /* ------------------------------------------------------------ *
   * 2) Reglas de negocio
   * ------------------------------------------------------------ */

  async function applyRules(cart) {
    if (isSyncing) {
      pendingResync = true;
      return;
    }

    let giftLine = findGiftLine(cart);
    const qualifies = cartQualifies(cart);

    // Regla: si el subtotal cae por debajo del umbral, quita el regalo.
    if (!qualifies && giftLine) {
      isSyncing = true;
      try {
        await changeLine(giftLine.key, 0);
        cart = await fetchCart();
      } finally {
        isSyncing = false;
      }
    }

    // Resguardo: nunca más de 1 línea de regalo (ej. dos pestañas abiertas
    // a la vez, o un cambio manual).
    let giftLines = cart.items.filter(
      (i) => i.properties && i.properties[GIFT_PROPERTY_KEY] === 'true'
    );
    if (giftLines.length > 1) {
      isSyncing = true;
      try {
        for (let i = 1; i < giftLines.length; i++) {
          await changeLine(giftLines[i].key, 0);
        }
        cart = await fetchCart();
      } finally {
        isSyncing = false;
      }
    }

    // Resguardo: nunca más de 1 unidad en la línea de regalo (por si el
    // cliente sube la cantidad desde el propio stepper del carrito).
    const soloGift = findGiftLine(cart);
    if (soloGift && soloGift.quantity > 1) {
      isSyncing = true;
      try {
        await changeLine(soloGift.key, 1);
        cart = await fetchCart();
      } finally {
        isSyncing = false;
      }
    }

    render(cart, cartQualifies(cart));

    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
    await maybeRefreshSections();

    if (pendingResync) {
      pendingResync = false;
      syncFromCart();
    }
  }

  /* ------------------------------------------------------------ *
   * 3) Renderizado — solo mostramos/ocultamos markup ya construido
   *    (por el snippet Liquid y por buildModal()), no generamos
   *    HTML de producto desde JS.
   * ------------------------------------------------------------ */

  function render(cart, qualifies) {
    const giftLine = findGiftLine(cart);
    const { thresholdCents, minQty } = config();

    progressEls.forEach((progressEl) => {
      progressEl.hidden = qualifies;
      if (!qualifies) {
        const subtotal = nonGiftSubtotalCents(cart);
        const qty = nonGiftQuantity(cart);
        const moneyRemaining = Math.max(thresholdCents - subtotal, 0);
        const itemsRemaining = Math.max(minQty - qty, 0);

        const textEl = progressEl.querySelector('[data-gwp-progress-text]');
        if (textEl) {
          if (itemsRemaining > 0 && moneyRemaining > 0) {
            const itemWord = itemsRemaining === 1 ? 'producto' : 'productos';
            textEl.textContent =
              'Agrega ' +
              itemsRemaining +
              ' ' +
              itemWord +
              ' más, o compra ' +
              formatMoney(moneyRemaining, cart.currency) +
              ' más, y elige un regalo de cortesía.';
          } else {
            textEl.textContent = 'Agrega productos a tu carrito para desbloquear tu regalo de cortesía.';
          }
        }

        const fillEl = progressEl.querySelector('[data-gwp-fill]');
        if (fillEl) {
          // Progreso hacia lo que esté más cerca: cantidad de productos o monto.
          const qtyPct = minQty > 0 ? (qty / minQty) * 100 : 0;
          const moneyPct = thresholdCents > 0 ? (subtotal / thresholdCents) * 100 : 0;
          const pct = Math.min(100, Math.round(Math.max(qtyPct, moneyPct)));
          fillEl.style.width = pct + '%';
        }
      }
    });

    widgets.forEach((widgetEl) => {
      widgetEl.hidden = !qualifies;
    });

    if (qualifies) {
      widgets.forEach((widgetEl) => {
        const pickerEl = widgetEl.querySelector('[data-gwp-state="picker"]');
        const selectedEl = widgetEl.querySelector('[data-gwp-state="selected"]');

        if (giftLine) {
          pickerEl.hidden = true;
          selectedEl.hidden = false;
          const nameEl = selectedEl.querySelector('[data-gwp-selected-name]');
          const imgEl = selectedEl.querySelector('[data-gwp-selected-image]');
          if (nameEl) nameEl.textContent = giftLine.product_title || giftLine.title;
          if (imgEl && giftLine.image) {
            imgEl.src = giftLine.image;
            imgEl.alt = giftLine.product_title || giftLine.title;
          }
        } else {
          pickerEl.hidden = false;
          selectedEl.hidden = true;
        }
      });

      document.querySelectorAll('[data-gwp-select]').forEach((btn) => {
        btn.classList.toggle(
          'is-selected',
          !!giftLine && String(btn.dataset.variantId) === String(giftLine.variant_id || giftLine.id)
        );
      });
    }

    maybeShowModal(qualifies, giftLine);
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: currency || 'MXN',
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* ------------------------------------------------------------ *
   * 4) Acciones del usuario
   * ------------------------------------------------------------ */

  function bindPickerEvents() {
    widgets.forEach((widgetEl) => {
      widgetEl.addEventListener('click', async (e) => {
        const selectBtn = e.target.closest('[data-gwp-select]');
        const changeBtn = e.target.closest('[data-gwp-change]');

        if (selectBtn) {
          e.preventDefault();
          await selectGift(selectBtn.dataset.variantId);
        }

        if (changeBtn) {
          e.preventDefault();
          widgetEl.querySelector('[data-gwp-state="picker"]').hidden = false;
          widgetEl.querySelector('[data-gwp-state="selected"]').hidden = true;
        }
      });
    });
  }

  async function selectGift(variantId) {
    setStatus('Agregando tu regalo…');
    isSyncing = true;
    try {
      const cart = await fetchCart();
      const existingGift = findGiftLine(cart);

      if (existingGift && String(existingGift.variant_id) !== String(variantId)) {
        await changeLine(existingGift.key, 0);
      }
      if (!existingGift || String(existingGift.variant_id) !== String(variantId)) {
        await addGift(variantId);
      }
      setStatus('¡Regalo agregado a tu carrito!');
      closeModal();
    } catch (err) {
      console.error('[GWP] Error al seleccionar el regalo:', err);
      setStatus('No pudimos agregar el regalo. Intenta de nuevo.');
    } finally {
      isSyncing = false;
      syncFromCart();
    }
  }

  function addGift(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: { [GIFT_PROPERTY_KEY]: 'true' },
          },
        ],
      }),
    }).then((r) => {
      if (!r.ok) throw new Error('add.js failed');
      return r.json();
    });
  }

  function changeLine(lineKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    }).then((r) => {
      if (!r.ok) throw new Error('change.js failed');
      return r.json();
    });
  }

  function setStatus(msg) {
    // Actualiza el mensaje de estado en todas las instancias del widget
    // (drawer, página de carrito) y también en la ventana emergente,
    // ya que su elemento de estado comparte el mismo atributo.
    document.querySelectorAll('[data-gwp-status]').forEach((el) => {
      el.textContent = msg;
    });
  }

  /* ------------------------------------------------------------ *
   * 5) Ventana emergente (modal) — se construye una sola vez a
   *    partir del propio markup del selector (clonando las opciones
   *    ya renderizadas por Liquid, para no duplicar información en
   *    dos lugares) y se muestra automáticamente en el momento en
   *    que el carrito cruza el umbral sin un regalo elegido.
   * ------------------------------------------------------------ */

  function buildModal() {
    const optionsSource = widgets[0] && widgets[0].querySelector('.gwp__options');
    if (!optionsSource) return;

    const overlay = document.createElement('div');
    overlay.className = 'gwp-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'gwp-modal-title');
    overlay.hidden = true;

    const dialog = document.createElement('div');
    dialog.className = 'gwp-modal';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'gwp-modal__close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.innerHTML = '&times;';

    const header = document.createElement('div');
    header.className = 'gwp-modal__header';
    header.innerHTML =
      '<p class="gwp__eyebrow">Regalo por tu compra</p>' +
      '<h3 class="gwp__title" id="gwp-modal-title">¡Felicidades! Elige tu regalo</h3>' +
      '<p class="gwp__subtitle">Tu compra ya califica para una loción para manos y cuerpo de 30&nbsp;ml de cortesía. Elige tu aroma:</p>';

    const optionsClone = optionsSource.cloneNode(true);
    optionsClone.classList.add('gwp-modal__options');

    const status = document.createElement('p');
    status.className = 'gwp__status gwp-modal__status';
    status.setAttribute('data-gwp-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'gwp-modal__dismiss';
    dismiss.textContent = 'Elegir más tarde';

    dialog.appendChild(closeBtn);
    dialog.appendChild(header);
    dialog.appendChild(optionsClone);
    dialog.appendChild(status);
    dialog.appendChild(dismiss);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    closeBtn.addEventListener('click', closeModal);
    dismiss.addEventListener('click', closeModal);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeModal();
    });
    dialog.addEventListener('click', async (e) => {
      const selectBtn = e.target.closest('[data-gwp-select]');
      if (selectBtn) {
        e.preventDefault();
        await selectGift(selectBtn.dataset.variantId);
      }
    });

    modalOverlay = overlay;
  }

  function onModalKeydown(e) {
    if (e.key === 'Escape') closeModal();
  }

  function openModal() {
    if (!modalOverlay || !modalOverlay.hidden) return;
    modalOverlay.hidden = false;
    // Forzar reflow antes de agregar la clase para que la transición corra.
    // eslint-disable-next-line no-unused-expressions
    modalOverlay.offsetHeight;
    modalOverlay.classList.add('is-open');
    document.documentElement.classList.add('gwp-modal-open');
    document.addEventListener('keydown', onModalKeydown);
    const closeBtn = modalOverlay.querySelector('.gwp-modal__close');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!modalOverlay || modalOverlay.hidden) return;
    modalOverlay.classList.remove('is-open');
    document.documentElement.classList.remove('gwp-modal-open');
    document.removeEventListener('keydown', onModalKeydown);
    setTimeout(() => {
      if (modalOverlay && !modalOverlay.classList.contains('is-open')) {
        modalOverlay.hidden = true;
      }
    }, 220);
  }

  // Decide si hay que mostrar la ventana emergente: solo se abre en el
  // momento exacto en que el carrito "cruza" el umbral sin regalo elegido
  // (es decir, no estaba calificando o ya tenía regalo, y ahora califica
  // y no tiene regalo). Si la persona la cierra sin elegir, no se vuelve
  // a abrir sola — queda el selector normal del carrito como recordatorio,
  // hasta que el carrito vuelva a cruzar el umbral (bajó y volvió a subir,
  // o se quitó el regalo elegido).
  function maybeShowModal(qualifies, giftLine) {
    const needsGift = !!qualifies && !giftLine;
    let prevNeedsGift = false;
    try {
      prevNeedsGift = sessionStorage.getItem(MODAL_FLAG_KEY) === '1';
    } catch (e) {
      // Almacenamiento no disponible (modo privado, etc.) — la ventana
      // podría abrirse más de una vez por sesión, pero el widget normal
      // sigue funcionando igual.
    }

    if (needsGift && !prevNeedsGift) {
      openModal();
    } else if (!needsGift) {
      closeModal();
    }

    try {
      sessionStorage.setItem(MODAL_FLAG_KEY, needsGift ? '1' : '0');
    } catch (e) {}
  }

  /* ------------------------------------------------------------ *
   * 6) Opcional: refrescar visualmente el resto del carrito del
   *    tema (drawer, ícono del carrito, lista de productos) vía la
   *    Section Rendering API, si se configuró data-refresh-sections
   *    en el snippet Liquid. Si no se configuró, este paso se omite
   *    silenciosamente — la lógica del regalo funciona igual.
   * ------------------------------------------------------------ */

  async function maybeRefreshSections() {
    const { refreshSections } = config();
    if (!refreshSections.length) return;

    try {
      const params = new URLSearchParams({ sections: refreshSections.join(',') });
      const res = await fetch('/cart?' + params.toString(), {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const data = await res.json();
      Object.keys(data).forEach((sectionId) => {
        const container = document.getElementById('shopify-section-' + sectionId);
        if (!container) return;
        const parsed = new DOMParser().parseFromString(data[sectionId], 'text/html');
        const newInner = parsed.getElementById('shopify-section-' + sectionId);
        container.innerHTML = newInner ? newInner.innerHTML : parsed.body.innerHTML;
      });
    } catch (err) {
      // No es crítico — la lógica del regalo ya se aplicó correctamente.
      console.warn('[GWP] No se pudieron refrescar las secciones del carrito:', err);
    }
  }

  init();
})();
